/**
 * Whether this window draws its own titlebar, and the space that costs.
 *
 * # What this module is for
 *
 * The app does not add a titlebar. It removes the system one and puts the
 * window's controls at the right-hand end of the header row that was already
 * there — the 53px band holding the brand, the search box, the filter menu and
 * refresh. That is the whole reason this is worth doing: chrome above the mail
 * goes from about 85px to 53px, and the buttons cost no vertical space at all
 * because they sit in a row that already existed.
 *
 * What it does cost is *horizontal* space at the right-hand end of whichever
 * element reaches the window's right edge — and that element is not one
 * element. With tabs open it is the tab strip's menu button. Without them, on a
 * wide window, it is the reader's toolbar. With the reading pane off it is the
 * list's Refresh button. In settings it is the panel's own header. On a phone
 * layout it is whichever single pane is showing. So the gutter is published
 * here as a custom property and each of those reserves it in the stylesheet,
 * rather than every one of them being taught about window buttons.
 *
 * macOS is the mirror image: the app draws nothing there, but the system's
 * traffic lights overlay the top-*left* corner of the same band, so the same
 * mechanism publishes a left-hand gutter — reserved by whichever element
 * reaches the window's left edge, which is just as much "not one element" as
 * the right was.
 *
 * # Why a signal and not a media query
 *
 * Because the *height* of the topmost band changes with the tab count and the
 * buttons have to centre in it, and because whether there are buttons at all is
 * a per-platform, per-preference fact that CSS cannot see.
 *
 * # The one failure mode to keep in mind
 *
 * An undefined custom property is dropped silently — no error, no warning, the
 * declaration simply does not apply. This project has already lost hours to
 * exactly that. So every property below is given a value in `:root` as well,
 * and the gutter rules are written as `calc(<their own padding> + var(--…))`,
 * so a variable that fails to resolve degrades to **today's padding**, never to
 * a button sitting on top of a control.
 */

import { computed, effect, signal } from "@preact/signals";
import { windowChrome } from "@transport";

import * as tabs from "./tabs";
import { systemTitlebar } from "./settings";

/**
 * Whether the platform left the system titlebar in place.
 *
 * `null` until asked, which is why {@link probe} runs before the first render:
 * a page that painted its own buttons and then discovered the OS was already
 * drawing some would show two sets for a frame.
 */
const decorated = signal<boolean | null>(null);

/**
 * Whether this build can draw a titlebar at all.
 *
 * False in a browser, and false on any platform where the Rust side chose to
 * keep the system titlebar — today that is macOS, where the right answer is a
 * transparent bar with the real traffic lights over it rather than buttons of
 * our own. Read off the window instead of from a second copy of the `cfg`, so
 * it cannot disagree with what Rust actually did.
 */
export const available = computed(() => windowChrome !== null && decorated.value === false);

/**
 * Whether the app is drawing the titlebar right now.
 *
 * `available` says the platform allows it; this says the platform allows it
 * *and* nobody has asked for the system one back.
 */
export const drawing = computed(() => available.value && !systemTitlebar.value);

/** True while the window is maximised, so the button can show the right glyph. */
export const maximized = signal(false);

/**
 * Ask the window what it is, once, before anything is painted.
 *
 * Called from `main.tsx` alongside the device token, for the same reason that
 * one is awaited: a platform fact that arrives after the first paint is a
 * platform fact somebody sees the app get wrong.
 */
export async function probe(): Promise<void> {
  // Bound once so the narrowing survives the `await`s below: `windowChrome` is
  // a module import and TypeScript will not carry a null check across them.
  const chrome = windowChrome;
  if (!chrome) return;
  try {
    decorated.value = await chrome.isDecorated();
    maximized.value = await chrome.isMaximized();
  } catch {
    // A window that will not answer is one we do not draw for. Leaving
    // `decorated` null means `available` stays false and the system titlebar --
    // whatever it is -- remains the only chrome, which is the safe direction.
    return;
  }

  // The size changing is how being maximised is noticed: there is no maximise
  // event, and the button is not the only route in. A double-click on the drag
  // region and KWin's own tiling shortcuts both arrive here.
  chrome.onResized(() => {
    void chrome.isMaximized().then((now) => (maximized.value = now));
  });

  // The stored preference, applied now that we know the platform allows it.
  // Rust built this window before the page existed and could not read
  // localStorage, so it used the platform default; this is where an override
  // takes effect. Anybody who has not overridden it sees nothing happen.
  if (systemTitlebar.peek()) void chrome.setDecorations(true);
}

/**
 * Turn the system titlebar on or off, and remember which.
 *
 * Live rather than on restart, because a setting that needs the app closed to
 * take effect is one nobody believes worked.
 */
export async function useSystemTitlebar(on: boolean): Promise<void> {
  systemTitlebar.value = on;
  if (windowChrome) await windowChrome.setDecorations(on);
}

/**
 * How wide the window controls are, including the gap before them.
 *
 * Three 30px buttons and 4px of gap each, plus 8px of breathing room at the
 * window edge. Written here rather than measured, because the stylesheet has to
 * reserve the space before the buttons have laid out — measuring would reserve
 * the right amount one frame late, which is one frame of a button on top of
 * Refresh.
 */
const CONTROLS_WIDTH = 3 * 34 + 8;

/**
 * How wide macOS's traffic lights are, including the margin around them.
 *
 * Written rather than measured for a stronger reason than {@link CONTROLS_WIDTH}
 * has: these buttons are not ours, there is no element to measure. Three 12px
 * circles on 20px centres starting 12px in, plus breathing room before the
 * first thing that is ours.
 */
const TRAFFIC_LIGHTS_WIDTH = 78;

/**
 * Whether the platform is drawing traffic lights over our top-left corner.
 *
 * On macOS the Rust side keeps the system titlebar and makes it an overlay —
 * see `chrome.rs` — so the app never draws controls of its own there
 * ({@link available} stays false) but the *left* end of the top band is under
 * buttons all the same. A constant, not a signal: the overlay is there from the
 * first frame and no preference removes it. The `typeof` guard is for the test
 * runner's plain-node environment, which has a `navigator` of its own but no
 * window to have chrome on.
 */
const TRAFFIC_LIGHTS =
  windowChrome !== null && typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

/** What the effect below writes: two gutters, and which band carries them. */
export interface Gutter {
  left: string;
  right: string;
  band: "tabs" | "heads" | null;
}

/**
 * The gutter decision, separated from the DOM it is applied to.
 *
 * Which band the buttons sit in, so only that one reserves the space. With a
 * tab strip our own controls are *inside* it, as flex children, and flex
 * reserves their width by itself -- so the right-hand answer there is "nobody
 * reserves anything", which is what `data-chrome-band="tabs"` means in the
 * stylesheet. The traffic lights are nobody's flex children, so the *left*
 * gutter is reserved in either band; the attribute says which one.
 *
 * Zero, not absent, on the sides that have nothing. Every gutter rule is
 * `calc(<its own> + var(--…))`, so a zero reproduces today's padding exactly --
 * which is the property the browser build depends on, and the one worth
 * asserting: not "similar", the same.
 */
export function gutter(drawing: boolean, trafficLights: boolean, hasTabs: boolean): Gutter {
  return {
    left: trafficLights ? `${TRAFFIC_LIGHTS_WIDTH}px` : "0px",
    right: drawing ? `${CONTROLS_WIDTH}px` : "0px",
    band: drawing || trafficLights ? (hasTabs ? "tabs" : "heads") : null,
  };
}

/**
 * The gutter, and which band is carrying it.
 *
 * Published on the root element so the stylesheet can reserve it without any
 * rule knowing how many tabs are open. Applied at import for the reason
 * `lib/panes.ts` gives for the same pattern: the root element exists before
 * this module is parsed, and waiting for a render would paint the wrong padding
 * first. Guarded because under vitest's node environment there is no document
 * at all — the decision above is what the tests exercise, and this is only its
 * application.
 */
if (typeof document !== "undefined") {
  effect(() => {
    const style = document.documentElement.style;
    const root = document.documentElement;
    const g = gutter(drawing.value, TRAFFIC_LIGHTS, tabs.hasTabs.value);

    style.setProperty("--chrome-left", g.left);
    style.setProperty("--chrome-right", g.right);
    if (g.band === null) root.removeAttribute("data-chrome-band");
    else root.setAttribute("data-chrome-band", g.band);
  });
}
